import { InjectionToken } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { chain, isEmpty } from 'lodash';
import { 
    BooleanAgFloatingFilterComponent, 
    booleanQuickFilterText, 
    booleanRenderer, 
    CovanceGridColumn, 
    dateRenderer, 
    dateTimeComparator, 
    dateTimeRenderer, 
    ViewHistoryAgRendererComponent, 
    BooleanCellRendererParams, 
    yesNoRenderer, 
    BooleanAgFloatingFilterParams, 
    dateTimeArrayComparator, 
    dateArrayRenderer } from '../../covance-ag-grid';
import { GroupingOptionItem } from '../../grouping-selector';
import { LanguageLinkAgRendererComponent } from '../language-link/language-link-ag-renderer.component';
import { Category } from '../models/category';
import { LabManualsReport, LabReport, Report, SiteActionsReport, VisitReport, TrainingMaterialsReport } from '../models/report';
import { comparators } from '../../core';
import { Moment } from 'moment';

export interface DocumentsColumns {
    getColumnDefinitions(category: Category | undefined): ColDef[];
    hasStatusColumn(category: string): boolean;
    getGroupedColumns(category: string): GroupingOptionItem[];
}

export const DOCUMENTS_COLUMNS_TOKEN = new InjectionToken<DocumentsColumns>('DocumentsColumns');

export const selectColumn: ColDef = {
    headerName: 'Select',
    width: 75,
    checkboxSelection: true,
    comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any): number => {
        return nodeB.selected - nodeA.selected;
    }
};

export const statusColumn: CovanceGridColumn = {
    headerName: 'Status',
    field: 'viewHistory',
    width: 75,
    cellRendererFramework: ViewHistoryAgRendererComponent,
};

export const titleColumn: ColDef = {
    headerName: 'Title',
    field: 'title',
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter',
    comparator: (a, b) => comparators.mixedStringComparator(a, b)
};

export const categoryColumn: ColDef = {
    headerName: 'Category',
    field: 'subcategoryTitle',
    filter: 'agSetColumnFilter',
};

export const letterNumberColumn: ColDef = {
    headerName: 'Letter Number',
    field: 'letterNumber',
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter',
    comparator: (a, b) => comparators.mixedStringComparator(a, b)
};

export const postedColumn: CovanceGridColumn = {
    sort: 'desc',
    headerName: 'Posted',
    field: 'postedDateTime',
    comparator: dateTimeComparator,
    valueGetter: (params: ReportParams) => {
        if (!params.data) {
            return null;
        }
        return dateTimeRenderer({ value: params.data.postedDateTime });
    },
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter'
};

export const versionNumberColumn: ColDef = {
    headerName: 'Version',
    field: 'version'
};

export const versionDateColumn: ColDef = {
    headerName: 'Version Date',
    field: 'versionDate',
    comparator: dateTimeComparator,
    valueGetter: (params: ReportParams) => {
        if (!params.data) {
            return null;
        }

        let data = params.data as LabManualsReport;

        return dateRenderer({ value: data.versionDate });
    }
};

export const zcProjectNumberColumn: ColDef = {
    headerName: 'Project',
    field: 'zcProjectNumber',
    filter: 'agTextColumnFilter',
    filterParams: { filterOptions: ['startsWith'] },
    floatingFilterComponentParams: { suppressFilterButton: true },
    width: 115
};

export const trainingMaterialsProjectColumn: ColDef = {
    valueGetter: (params: TrainingMaterialsReportRendererParams) => params.data.projectNumber,
    headerName: 'Project Number',
    field: 'zcProjectNumber',
    filter: 'agTextColumnFilter',
    filterParams: { filterOptions: ['startsWith'] },
    floatingFilterComponentParams: { suppressFilterButton: true },
    width: 115
};


export const regionColumn: ColDef = {
    headerName: 'Region',
    field: 'region',
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true }
};

export const sitesColumn: ColDef = {
    headerName: 'Sites',
    field: 'sites',
    valueGetter: (params: { data: Report }) => params.data.sites.map(fl => fl.siteNum).join(', '),
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter'
};

export function getSiteNumColumn(sort: 'asc' | 'desc' | null = null): ColDef {
    return {
        headerName: 'Site Number',
        field: 'siteNum',
        suppressMenu: true,
        filter: 'agTextColumnFilter',
        sort: sort,
        floatingFilterComponentParams: { suppressFilterButton: true },
        comparator: (a, b) => comparators.mixedStringComparator(a, b),
    };
}

export const visitColumn: ColDef = {
    headerName: 'Visit',
    field: 'visit',
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter',
    valueGetter: (params: VisitReportRendererParams) => {
        if (!params.data) {
            return null;
        }

        const visits = chain(params.data.visits)
            .filter(x => !isEmpty(x.visit))
            .map(x => x.visit)
            .sort()
            .value()
            .join(', ');

        return visits;
    }
};

export const visitCollectionDateColumn: CovanceGridColumn = {
    headerName: 'Collected Date',
    field: 'visitCollectionDate',
    comparator: dateTimeArrayComparator,
    valueGetter: (params: VisitReportRendererParams) => {
        if (!params.data) {
            return null;
        }

        const collectionDates = chain(params.data.visits)
            .filter(x => !isEmpty(x.visitCollectionDate))
            .map(x => x.visitCollectionDate)
            .sortBy(x => x.valueOf())
            .value();

        return collectionDates;
    },
    cellRenderer: (params: {value: Moment[]}) => dateArrayRenderer(params.value),
    getQuickFilterText: (params: {value: Moment[]}) => dateArrayRenderer(params.value),
};

export const isResponseRequiredColumn: ColDef = {
    headerName: 'Response Required',
    field: 'isResponseRequired',
    width: 150,
    cellRenderer: booleanRenderer,
    getQuickFilterText: booleanQuickFilterText,
    filter: 'agSetColumnFilter',
    filterParams: {
        cellRenderer: (params: BooleanCellRendererParams) => yesNoRenderer(params.value)
    },
    floatingFilterComponentFramework: BooleanAgFloatingFilterComponent,
    floatingFilterComponentParams: {
        booleanValueRenderer: yesNoRenderer
    } as BooleanAgFloatingFilterParams
};

export const responseNeededByColumn: CovanceGridColumn = {
    headerName: 'Response Needed by',
    field: 'responseNeededBy',
    comparator: dateTimeComparator,
    valueGetter: (params: SiteActionsReportParams) => {
        if (!params.data) {
            return null;
        }
        return dateRenderer({ value: params.data.responseNeededBy });
    },
    floatingFilterComponentParams: { suppressFilterButton: true },
    filter: 'agTextColumnFilter'
};

function getDownloadColumn(headerName: string): CovanceGridColumn {
    return {
        headerName: headerName,
        getQuickFilterText: (params: ReportParams) => params.data.fileLinks.map(fl => fl.language).join(' '),
        suppressSorting: true,
        suppressMenu: true,
        suppressFilter: true,
        cellRendererFramework: LanguageLinkAgRendererComponent,
        noExport: true,
        cellStyle: function () {
            return { overflow: 'visible' };
        }
    };
}

export const downloadColumn: CovanceGridColumn = getDownloadColumn('Download');

export const viewColumn: CovanceGridColumn = getDownloadColumn('View');

export const downloadViewColumn: CovanceGridColumn = getDownloadColumn('Download/View');

export const reportTypeColumn: CovanceGridColumn = {
    headerName: 'Type',
    field: 'type',
    width: 75
};

type ReportParams = {
    data: Report;
};

export type VisitReportRendererParams = {
    data: VisitReport;
};

export type TrainingMaterialsReportRendererParams = {
    data: TrainingMaterialsReport;
};

export type SiteActionsReportParams = {
    data: SiteActionsReport;
};

export type LabReportParams = {
    data: LabReport;
};
